import { ChevronDown, Info, TriangleAlert, X, XCircle } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { HowToStartDialog } from "./HowToStartDialog";
import { useShellData } from "./shellData";

interface BannerDef {
  id: string;
  variant: "info" | "warning" | "error";
  text: ReactNode;
  action?: ReactNode;
  dismiss?: () => void;
}

const VARIANT = {
  info: { cls: "bg-tone-blue-bg text-tone-blue-fg border-tone-blue-border", icon: Info, role: "status" },
  warning: { cls: "bg-tone-amber-bg text-tone-amber-fg border-tone-amber-border", icon: TriangleAlert, role: "status" },
  error: { cls: "bg-tone-red-bg text-tone-red-fg border-tone-red-border", icon: XCircle, role: "alert" },
} as const;

function readDismissed(): boolean {
  try {
    return localStorage.getItem("ats.pref.viewerBannerDismissed") === "true";
  } catch {
    return false;
  }
}

export function Banners() {
  const s = useShellData();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [howTo, setHowTo] = useState(false);
  const [viewerDismissed, setViewerDismissed] = useState(readDismissed);
  const list: BannerDef[] = [];

  if (s.runtime.state === "not_running")
    list.push({
      id: "runtime",
      variant: "warning",
      text: "Runtime not connected. Start ATS on this computer to run or view tests.",
      action: (
        <Button size="sm" variant="outline" onClick={() => setHowTo(true)}>
          How to start ATS
        </Button>
      ),
    });
  if (s.runtime.state === "recovering")
    list.push({
      id: "recovering",
      variant: "warning",
      text: "ATS is recovering after a restart. Browsing works; starting or cancelling runs is paused.",
    });
  if (s.runtime.state === "incompatible")
    list.push({
      id: "incompatible",
      variant: "error",
      text: `This UI needs runtime API ${s.runtime.required ?? "v1"}; the runtime on this machine is ${s.runtime.version ?? "?"} with API ${s.runtime.apiVersion ?? "?"}. Install the latest ATS runtime.`,
      action: (
        <Button size="sm" variant="outline" asChild>
          <Link to="/help#start">Download / Docs</Link>
        </Button>
      ),
    });
  if (s.adminApi === "unreachable")
    list.push({
      id: "admin",
      variant: "warning",
      text: `Can't reach the Admin API. Your role keeps working for up to ${s.cachedRoleMinutesLeft} minutes.`,
      action: (
        <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      ),
    });
  if (!s.online) list.push({ id: "offline", variant: "warning", text: "You're offline. Live updates resume when you reconnect." });
  if (s.activeRun)
    list.push({
      id: "active",
      variant: "info",
      text: "A run is in progress on this machine.",
      action: (
        <Button size="sm" variant="outline" onClick={() => navigate(`/runs/${s.activeRun!.runId}`)}>
          Open run
        </Button>
      ),
    });
  if (s.user?.role === "VIEWER" && !viewerDismissed)
    list.push({
      id: "viewer",
      variant: "info",
      text: "You have view-only access. Ask an admin for the Tester role to run tests.",
      dismiss: () => {
        try {
          localStorage.setItem("ats.pref.viewerBannerDismissed", "true");
        } catch {
          return;
        } finally {
          setViewerDismissed(true);
        }
      },
    });

  const shown = expanded ? list : list.slice(0, 2);
  const hidden = list.length - shown.length;

  return (
    <>
      {list.length > 0 && (
        <div className="flex flex-col no-print">
          {shown.map((b) => {
            const v = VARIANT[b.variant];
            return (
              <div key={b.id} role={v.role} className={cn("flex items-center gap-3 border-b px-4 py-2 text-sm", v.cls)}>
                <v.icon aria-hidden className="size-4 shrink-0" />
                <span className="flex-1">{b.text}</span>
                {b.action}
                {b.dismiss && (
                  <Button size="icon" variant="ghost" aria-label="Dismiss" onClick={b.dismiss}>
                    <X aria-hidden />
                  </Button>
                )}
              </div>
            );
          })}
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="flex items-center justify-center gap-1 border-b py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              +{hidden} more <ChevronDown aria-hidden className="size-3" />
            </button>
          )}
        </div>
      )}
      <HowToStartDialog open={howTo} onOpenChange={setHowTo} />
    </>
  );
}
